import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardActiveRunDto } from '@aiai/shared';
import { Badge, LiveDot } from './Badge';
import { formatCost, formatDuration } from '../lib/format';
import { workspacePath } from '../lib/workspacePath';

/** Segmented progress meter: 12 fixed segments, 6px tall, 3px gap. */
function SegmentedProgress({ value, max }: { value: number; max: number }) {
  const SEGS = 12;
  const pct = value / Math.max(max, 1);
  const completedCount = pct >= 1 ? SEGS : Math.floor(pct * SEGS);
  const hasActive = completedCount < SEGS;
  return (
    <div
      className="flex gap-[3px]"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemax={max}
    >
      {Array.from({ length: SEGS }, (_, i) => {
        if (i < completedCount) {
          return <div key={i} className="h-[6px] flex-1 rounded-[2px] bg-ink" />;
        }
        if (hasActive && i === completedCount) {
          return (
            <div
              key={i}
              className="h-[6px] flex-1 rounded-[2px] bg-signal animate-pulse-soft motion-reduce:animate-none"
            />
          );
        }
        return <div key={i} className="h-[6px] flex-1 rounded-[2px] border border-line bg-surface-2" />;
      })}
    </div>
  );
}

/** Ticker-style log line display: fades in on change, monospace, truncated. */
function LogLineTicker({ text }: { text: string | null }) {
  const prevText = useRef(text);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (text !== prevText.current) {
      prevText.current = text;
      setKey((k) => k + 1);
    }
  }, [text]);

  if (!text) {
    return (
      <span className="font-mono text-xs text-ink-faint italic">starting…</span>
    );
  }

  return (
    <span
      key={key}
      className="block truncate font-mono text-xs text-ink-muted animate-fade-in motion-reduce:animate-none"
      title={text}
    >
      {text}
    </span>
  );
}

/** Enhanced "Now" card for a single active run — links to the issue it's working. */
export function NowCard({ run, projectName }: { run: DashboardActiveRunDto; projectName: string }) {
  // Ticking clock so elapsed time updates every second without a parent re-render.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const hasCost = run.totalCostUsd != null && run.totalCostUsd > 0;
  // Chat (issue-less) runs have nothing to link to — render the same card
  // as a non-navigating div instead of a broken `/issues/null` link.
  const issueRef = run.issueCode ?? run.issueId;
  const cardClassName = 'card flex flex-col gap-2 p-3.5 transition-colors hover:border-line-strong hover:bg-surface-2';

  const content = (
    <>
      {/* Header row: workspace · repo + badges */}
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <LiveDot />
          <span className="truncate text-2xs font-medium text-ink-muted" title={run.repoFullName}>
            <span className="text-ink-faint">{run.workspace.name} · </span>
            {projectName}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {run.loopCount > 0 && (
            <Badge tone="warning" size="sm">retry {run.loopCount}</Badge>
          )}
          <Badge tone="brand" size="sm" pulse>
            {run.currentStageRole ?? 'starting'}
          </Badge>
        </span>
      </div>

      {/* Issue code + title */}
      <span className="text-sm font-semibold leading-snug text-ink">
        {run.issueCode && (
          <span className="mr-1.5 font-mono text-2xs uppercase tracking-wide text-ink-faint">
            {run.issueCode}
          </span>
        )}
        {run.issueTitle}
      </span>

      {/* Live log ticker */}
      <div className="min-h-[1.125rem]">
        <LogLineTicker text={run.lastLogLine} />
      </div>

      {/* Progress bar */}
      <SegmentedProgress value={run.currentStageOrder} max={run.totalStages} />

      {/* Footer: stage counter + elapsed + cost */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-2xs uppercase tracking-[0.08em] text-ink-faint">
          {run.currentStageOrder}/{run.totalStages}
        </span>
        <span className="flex items-center gap-3 font-mono text-2xs tabular-nums text-ink-muted">
          {hasCost && (
            <span className="text-ink-faint" title="Cost so far">
              {formatCost(run.totalCostUsd)}
            </span>
          )}
          {/* formatDuration uses Date.now() when end is null; 1s ticker re-renders keep it fresh. */}
          {formatDuration(run.startedAt ?? run.createdAt, null)}
        </span>
      </div>
    </>
  );

  if (!issueRef) {
    return <div className={cardClassName}>{content}</div>;
  }
  return (
    <Link to={workspacePath(run.workspace.slug, `/issues/${issueRef}`)} className={cardClassName}>
      {content}
    </Link>
  );
}
